import { Tag } from 'lucide-react';
import type { DiscountWithStats } from '@respectly/shared-types';

// ============================================
// Status Badge (derived from data, not a DB field)
// ============================================

export function getDiscountStatus(
  discount: Pick<DiscountWithStats, 'isActive' | 'expiresAt' | 'usedCount' | 'maxUses'>
): 'active' | 'expired' | 'exhausted' | 'inactive' {
  if (!discount.isActive) return 'inactive';
  if (discount.expiresAt && new Date(discount.expiresAt) < new Date()) return 'expired';
  if (discount.maxUses !== null && discount.maxUses !== undefined && discount.usedCount >= discount.maxUses)
    return 'exhausted';
  return 'active';
}

export function DiscountStatusBadge({
  discount,
}: {
  discount: Pick<DiscountWithStats, 'isActive' | 'expiresAt' | 'usedCount' | 'maxUses'>;
}) {
  const status = getDiscountStatus(discount);

  const map = {
    active: {
      label: 'Active',
      className:
        'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
    },
    expired: {
      label: 'Expired',
      className:
        'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
    },
    exhausted: {
      label: 'Limit Reached',
      className:
        'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
    },
    inactive: {
      label: 'Inactive',
      className:
        'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    },
  } as const;

  const { label, className } = map[status];

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${className}`}
    >
      {label}
    </span>
  );
}

// ============================================
// Scope Badge
// ============================================

export function DiscountScopeBadge({ scope }: { scope: DiscountWithStats['scope'] }) {
  const map = {
    all_plans: {
      label: 'All Plans',
      className:
        'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
    },
    specific_plans: {
      label: 'Specific Plans',
      className:
        'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800',
    },
    account: {
      label: 'Account-Specific',
      className:
        'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
    },
  } as const;

  const { label, className } = map[scope];

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${className}`}
    >
      {label}
    </span>
  );
}

// ============================================
// Promo Code Badge (shown when code is set)
// ============================================

export function DiscountCodeBadge({ code }: { code: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800 font-mono">
      <Tag className="h-3 w-3 shrink-0" />
      {code}
    </span>
  );
}
